import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/table";
import { MetricRow } from "@/http/api-types";
import React from "react";
import { formatValue } from "./columns";

export interface BusinessSection {
  title: string;
  rows: MetricRow[];
}

interface BusinessMetricsTableProps {
  months: string[];
  sections: BusinessSection[];
}

export const BusinessMetricsTable: React.FC<BusinessMetricsTableProps> = ({ months, sections }) => {
  return (
    <div className="border rounded-md overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px] min-w-[300px] sticky left-0 z-20 bg-background shadow-[1px_0_0_0_hsl(var(--border))]">
              Metric
            </TableHead>
            <TableHead className="w-[100px] min-w-[100px] text-right sticky left-[300px] z-20 bg-background shadow-[1px_0_0_0_hsl(var(--border))]">
              Low
            </TableHead>
            <TableHead className="w-[100px] min-w-[100px] text-right sticky left-[400px] z-20 bg-background shadow-[1px_0_0_0_hsl(var(--border))]">
              High
            </TableHead>
            {months.map(month => (
              <TableHead key={month} className="text-right min-w-[100px]">
                {month}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sections.map(section => (
            <React.Fragment key={section.title}>
              {/* Section Header Row */}
              <TableRow className="bg-muted/90 hover:bg-muted/90">
                <TableCell className="font-bold py-3 text-base sticky left-0 z-20 bg-muted/90 shadow-[1px_0_0_0_hsl(var(--border))]">
                  {section.title}
                </TableCell>
                <TableCell className="sticky left-[300px] z-20 bg-muted/90 shadow-[1px_0_0_0_hsl(var(--border))]" />
                <TableCell className="sticky left-[400px] z-20 bg-muted/90 shadow-[1px_0_0_0_hsl(var(--border))]" />
                <TableCell colSpan={months.length} />
              </TableRow>
              {/* Metric Rows */}
              {section.rows.map(row => (
                <TableRow key={row.metric}>
                  <TableCell className="font-medium pl-6 sticky left-0 z-20 bg-background shadow-[1px_0_0_0_hsl(var(--border))]">
                    {row.metric}
                  </TableCell>
                  <TableCell className="text-right sticky left-[300px] z-20 bg-background shadow-[1px_0_0_0_hsl(var(--border))] text-muted-foreground">
                    {row.low_threshold_display ? (
                      <span className="text-xs">{row.low_threshold_display}</span>
                    ) : row.low_threshold !== undefined && row.low_threshold !== null ? (
                      formatValue(row.low_threshold, row.format)
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className="text-right sticky left-[400px] z-20 bg-background shadow-[1px_0_0_0_hsl(var(--border))] text-muted-foreground">
                    {row.high_threshold_display ? (
                      <span className="text-xs">{row.high_threshold_display}</span>
                    ) : row.high_threshold !== undefined && row.high_threshold !== null ? (
                      formatValue(row.high_threshold, row.format)
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  {months.map(month => {
                    const value = row.values[month];
                    let bgColor = "";
                    if (value !== undefined) {
                      // Logic:
                      // Green if value >= low_threshold
                      // Red if value >= high_threshold
                      // Note: Red has precedence over green as it is meant as a warning.

                      const highThreshold = row.high_thresholds?.[month] ?? row.high_threshold;
                      const lowThreshold = row.low_thresholds?.[month] ?? row.low_threshold;

                      if (highThreshold !== undefined && highThreshold !== null && value >= highThreshold) {
                        bgColor = "!bg-red-100 dark:!bg-red-900/70";
                      } else if (lowThreshold !== undefined && lowThreshold !== null && value >= lowThreshold) {
                        bgColor = "!bg-green-100 dark:!bg-green-900/70";
                      }
                    }

                    return (
                      <TableCell key={month} className={`text-right ${bgColor}`}>
                        {formatValue(value, row.format)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
